import * as React from 'react';
import { Dialog } from '@ampm-7en-ai/ui/components/Dialog';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { useToast } from '@/hooks/use-toast';
import { useCreateTopupRange, useUpdateTopupRange, type TopupRange } from '@/hooks/useTopupRanges';
import { CURRENCY_SYMBOL } from '@/lib/money';

/**
 * Replies priced by how many are bought.
 *
 * The bounds are what make a range work or quietly overlap another, so the
 * form checks them against each other rather than accepting any two numbers.
 */
export default function TopupRangeDialogV2({
  open,
  onOpenChange,
  editRange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editRange?: TopupRange | null;
}) {
  const { toast } = useToast();
  const create = useCreateTopupRange();
  const update = useUpdateTopupRange();
  const saving = create.isPending || update.isPending;

  const [name, setName] = React.useState('');
  const [min, setMin] = React.useState('');
  const [max, setMax] = React.useState('');
  const [perReply, setPerReply] = React.useState('');
  const [active, setActive] = React.useState(true);

  React.useEffect(() => {
    if (!open) return;
    setName(editRange?.name ?? '');
    setMin(editRange ? String(editRange.min_qty) : '');
    setMax(editRange ? String(editRange.max_qty) : '');
    setPerReply(editRange?.price_per_reply ?? '');
    setActive(editRange?.active ?? true);
  }, [open, editRange]);

  const lower = Number.parseInt(min, 10);
  const upper = Number.parseInt(max, 10);
  const rate = Number.parseFloat(perReply);

  const problem =
    Number.isFinite(lower) && Number.isFinite(upper) && upper <= lower
      ? 'The upper bound has to be above the lower one.'
      : null;

  const blocked = !name.trim() || !(lower >= 0) || !(upper > 0) || !(rate >= 0) || Boolean(problem);

  const save = async () => {
    if (blocked) return;
    const payload = { name: name.trim(), min_qty: lower, max_qty: upper, price_per_reply: perReply, active };
    try {
      if (editRange) await update.mutateAsync({ id: editRange.id, ...payload });
      else await create.mutateAsync(payload);
      toast({ title: `${payload.name} ${editRange ? 'updated' : 'created'}` });
      onOpenChange(false);
    } catch (failure) {
      toast({
        title: 'That didn’t save',
        description: failure instanceof Error ? failure.message : 'The range wasn’t saved.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editRange ? 'Edit range' : 'New range'}
      description="A price per reply, for a band of quantities."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving || blocked}>
            {saving ? 'Saving…' : editRange ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" htmlFor="range-name">
          <Input id="range-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="1k – 10k" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="From" htmlFor="range-min" hint="Replies, inclusive.">
            <Input id="range-min" type="number" min={0} value={min} onChange={(e) => setMin(e.target.value)} placeholder="1000" />
          </Field>
          <Field label="To" htmlFor="range-max" hint="Replies, inclusive.">
            <Input id="range-max" type="number" min={1} value={max} onChange={(e) => setMax(e.target.value)} placeholder="10000" />
          </Field>
        </div>

        {problem && (
          <Callout tone="warning" title="Those bounds don’t work">
            {problem}
          </Callout>
        )}

        <Field label={`Price per reply (${CURRENCY_SYMBOL})`} htmlFor="range-rate">
          <Input
            id="range-rate"
            type="number"
            min={0}
            step="0.0001"
            value={perReply}
            onChange={(e) => setPerReply(e.target.value)}
            placeholder="0.0400"
          />
        </Field>

        {Number.isFinite(lower) && Number.isFinite(upper) && Number.isFinite(rate) && !problem && (
          <p className="text-ui-xs text-ui-fg-muted">
            Someone buying the whole band pays {CURRENCY_SYMBOL}
            {(upper * rate).toFixed(2)}.
          </p>
        )}

        <ToggleRow
          label="Available to buy"
          description="Switched off, it stays set up but nobody is priced against it."
          checked={active}
          onCheckedChange={setActive}
        />
      </div>
    </Dialog>
  );
}
